import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ProviderService } from '../../service/provider.service';
import * as ProviderActions from '../actions/provider.action';

@Injectable()
export class ProviderEffects {
  loadProviders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderActions.loadProviders),
      mergeMap(() =>
        this.providerService.getProviders().pipe(
          map(providers => ProviderActions.loadProvidersSuccess({ providers })),
          catchError(error => of(ProviderActions.loadProvidersFailure({ error }))),
        ),
      ),
    ),
  );

  loadProviderWebsites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderActions.loadProviderWebsites),
      mergeMap(action =>
        this.providerService.getProviderWebsites(action.providerId).pipe(
          map(websites => ProviderActions.loadProviderWebsitesSuccess({ providerId: action.providerId, websites })),
          catchError(error => of(ProviderActions.loadProviderWebsitesFailure({ error }))),
        ),
      ),
    ),
  );

  createProviderWebsite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderActions.createProviderWebsite),
      mergeMap(action =>
        this.providerService.createProviderWebsite(action.providerId, action.websiteData).pipe(
          map(website => ProviderActions.createProviderWebsiteSuccess({ providerId: action.providerId, website })),
          catchError(error => of(ProviderActions.createProviderWebsiteFailure({ error }))),
        ),
      ),
    ),
  );

  updateProviderWebsite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderActions.updateProviderWebsite),
      mergeMap(action =>
        this.providerService.updateProviderWebsite(action.providerId, action.websiteId, action.websiteData).pipe(
          map(website => ProviderActions.updateProviderWebsiteSuccess({ providerId: action.providerId, website })),
          catchError(error => of(ProviderActions.updateProviderWebsiteFailure({ error }))),
        ),
      ),
    ),
  );

  deleteProviderWebsite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderActions.deleteProviderWebsite),
      mergeMap(action =>
        this.providerService.deleteProviderWebsite(action.providerId, action.websiteId).pipe(
          map(() =>
            ProviderActions.deleteProviderWebsiteSuccess({
              providerId: action.providerId,
              websiteId: action.websiteId,
            }),
          ),
          catchError(error => of(ProviderActions.deleteProviderWebsiteFailure({ error }))),
        ),
      ),
    ),
  );

  loadProviderContacts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderActions.loadProviderContacts),
      mergeMap(action =>
        this.providerService.getProviderContacts(action.providerId).pipe(
          map(contacts => ProviderActions.loadProviderContactsSuccess({ providerId: action.providerId, contacts })),
          catchError(error => of(ProviderActions.loadProviderContactsFailure({ error }))),
        ),
      ),
    ),
  );

  createProviderContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderActions.createProviderContact),
      mergeMap(action =>
        this.providerService.createProviderContact(action.providerId, action.contactData).pipe(
          map(contact => ProviderActions.createProviderContactSuccess({ providerId: action.providerId, contact })),
          catchError(error => of(ProviderActions.createProviderContactFailure({ error }))),
        ),
      ),
    ),
  );

  updateProviderContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderActions.updateProviderContact),
      mergeMap(action =>
        this.providerService.updateProviderContact(action.providerId, action.contactId, action.contactData).pipe(
          map(contact => ProviderActions.updateProviderContactSuccess({ providerId: action.providerId, contact })),
          catchError(error => of(ProviderActions.updateProviderContactFailure({ error }))),
        ),
      ),
    ),
  );

  deleteProviderContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderActions.deleteProviderContact),
      mergeMap(action =>
        this.providerService.deleteProviderContact(action.providerId, action.contactId).pipe(
          map(() =>
            ProviderActions.deleteProviderContactSuccess({
              providerId: action.providerId,
              contactId: action.contactId,
            }),
          ),
          catchError(error => of(ProviderActions.deleteProviderContactFailure({ error }))),
        ),
      ),
    ),
  );

  loadProviderLogins$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderActions.loadProviderLogins),
      mergeMap(action =>
        this.providerService.getProviderLogins(action.providerId).pipe(
          map(logins => ProviderActions.loadProviderLoginsSuccess({ providerId: action.providerId, logins })),
          catchError(error => of(ProviderActions.loadProviderLoginsFailure({ error }))),
        ),
      ),
    ),
  );

  createProviderLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderActions.createProviderLogin),
      mergeMap(action =>
        this.providerService.createProviderLogin(action.providerId, action.loginData).pipe(
          map(login => ProviderActions.createProviderLoginSuccess({ providerId: action.providerId, login })),
          catchError(error => of(ProviderActions.createProviderLoginFailure({ error }))),
        ),
      ),
    ),
  );

  updateProviderLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderActions.updateProviderLogin),
      mergeMap(action =>
        this.providerService.updateProviderLogin(action.providerId, action.loginId, action.loginData).pipe(
          map(login => ProviderActions.updateProviderLoginSuccess({ providerId: action.providerId, login })),
          catchError(error => of(ProviderActions.updateProviderLoginFailure({ error }))),
        ),
      ),
    ),
  );

  updateProviderLoginActiveFlag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderActions.updateProviderLoginActiveFlag),
      mergeMap(action =>
        this.providerService.updateProviderLoginActiveFlag(action.providerId, action.loginId, action.activeData).pipe(
          map(login => ProviderActions.updateProviderLoginActiveFlagSuccess({ providerId: action.providerId, login })),
          catchError(error => of(ProviderActions.updateProviderLoginActiveFlagFailure({ error }))),
        ),
      ),
    ),
  );

  deleteProviderLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderActions.deleteProviderLogin),
      mergeMap(action =>
        this.providerService.deleteProviderLogin(action.providerId, action.loginId).pipe(
          map(() => ProviderActions.deleteProviderLoginSuccess({ providerId: action.providerId, loginId: action.loginId })),
          catchError(error => of(ProviderActions.deleteProviderLoginFailure({ error }))),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private providerService: ProviderService,
  ) {}
}
